import { useState } from "react";
import { Input } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { formatMoney } from "../../lib/format";
import { minorToMajor } from "../../lib/money";
import type { Campaign } from "../../lib/types";
import { useSession } from "../auth/useSession";
import { useUpdateCampaign } from "./useCampaigns";

// Mirrors the storefront's defaults in app-shopify/storefront/src/tierMessages.ts:
// a blank message means the default, so these are shown, never saved.
const DEFAULT_PROGRESS = "Add {remaining} to get {ordinal} free gift worth up to {value}";
const DEFAULT_UNLOCKED = "You unlocked your {ordinal} free gift — choose it below";
const DEFAULT_COMPLETE = "You've unlocked all your free gifts";

const MESSAGE_HELP =
	"Shown above the gift progress bar in the mini cart and in the AOVtools progress bar block. Leave blank for the default. Use {remaining} for the amount still to spend, {ordinal} for the tier's place (1st, 2nd, 3rd) and {value} for the most a shopper can get from that tier: the dearest option of each gift they pick from, every item of a gift that gives them all.";

/** Matches the server's limit, so an over-long message cannot be typed rather than refused. */
const MAX_MESSAGE_LENGTH = 160;

const fillExample = (template: string, currency: string): string => {
	const tokens: Record<string, string> = {
		remaining: formatMoney(minorToMajor(1200), currency),
		ordinal: "2nd",
		value: formatMoney(minorToMajor(3000), currency),
	};
	return template.replace(/\{(\w+)\}/g, (token, name: string) => tokens[name] ?? token);
};

type Message = "tierProgressMessage" | "tierUnlockedMessage" | "tierCompleteMessage";

const FIELDS: readonly { key: Message; label: string; fallback: string }[] = [
	{ key: "tierProgressMessage", label: "On the way to a tier", fallback: DEFAULT_PROGRESS },
	{
		key: "tierUnlockedMessage",
		label: "Tier reached, gift not chosen",
		fallback: DEFAULT_UNLOCKED,
	},
	{ key: "tierCompleteMessage", label: "Every gift claimed", fallback: DEFAULT_COMPLETE },
];

type TierMessagesFieldProps = {
	campaign: Campaign;
};

export const TierMessagesField = ({ campaign }: TierMessagesFieldProps) => {
	const update = useUpdateCampaign(campaign.id);
	const session = useSession();
	const currency = session.data?.tenant.currencyCode ?? "USD";
	const toast = useToast();
	const [drafts, setDrafts] = useState<Record<Message, string>>({
		tierProgressMessage: campaign.tierProgressMessage ?? "",
		tierUnlockedMessage: campaign.tierUnlockedMessage ?? "",
		tierCompleteMessage: campaign.tierCompleteMessage ?? "",
	});

	const commit = (key: Message) => {
		const next = drafts[key].trim() || null;
		if (next === campaign[key]) return;
		update.mutate(
			{ [key]: next },
			{
				onSuccess: () => toast.push("Message saved"),
				onError: (error) => toast.push(error.message, "error"),
			},
		);
	};

	return (
		<section className="space-y-4 rounded-lg border border-line bg-surface p-4">
			<div>
				<LabelWithHint label="Progress messages">{MESSAGE_HELP}</LabelWithHint>
			</div>
			<div className="grid gap-4 sm:grid-cols-3">
				{FIELDS.map(({ key, label, fallback }) => (
					<label key={key} className="block space-y-1.5">
						<span className="block text-sm text-ink">{label}</span>
						<Input
							value={drafts[key]}
							onChange={(event) => setDrafts({ ...drafts, [key]: event.target.value })}
							onBlur={() => commit(key)}
							placeholder={fallback}
							maxLength={MAX_MESSAGE_LENGTH}
						/>
						<span className="block text-xs text-ink-muted">
							{fillExample(drafts[key].trim() || fallback, currency)}
						</span>
					</label>
				))}
			</div>
		</section>
	);
};
